import {
  useRecentlyViewed as useRecentlyViewedQuery,
  useTrackView,
  useRemoveRecentlyViewedItem,
  useClearRecentlyViewed,
} from "@/queries/recently-viewed";
import type { ResourceType, TrackViewInput } from "@/types";

/**
 * Composable for tracking and reading the current user's Recently Viewed items. Backed by the
 * `/api/me/recent` endpoint via TanStack Query — items follow the user across browsers/devices
 * and are scoped server-side to the authenticated user + current org.
 */
export function useRecentlyViewed() {
  const { items } = useRecentlyViewedQuery();
  const { trackView: trackViewMutation } = useTrackView();
  const { removeRecentlyViewed } = useRemoveRecentlyViewedItem();
  const { clearRecentlyViewed } = useClearRecentlyViewed();

  const trackView = (input: TrackViewInput) => {
    trackViewMutation(input);
  };

  const removeItem = (id: string, type: string) => {
    removeRecentlyViewed({ id, type });
  };

  const clearAll = () => {
    clearRecentlyViewed();
  };

  /** Top-N recents, newest first. Server already returns them ordered. */
  const recentItems = (limit?: number) =>
    limit ? items.value.slice(0, limit) : items.value;

  const getIconForType = (type: ResourceType): string => {
    const iconMap: Record<ResourceType, string> = {
      project: "bi-boxes",
      workflow: "bi-diagram-3",
      connection: "bi-link",
      schedule: "bi-calendar",
      product: "bi-grid",
      subscriber: "bi-people",
      organisation: "bi-building",
      "email-template": "bi-envelope",
    };
    return iconMap[type] || "bi-file-earmark";
  };

  const getBadgeClassForType = (type: ResourceType): string => {
    const badgeMap: Record<ResourceType, string> = {
      project: "bg-primary",
      workflow: "bg-info",
      connection: "bg-success",
      schedule: "bg-warning",
      product: "bg-purple",
      subscriber: "bg-secondary",
      organisation: "bg-dark",
      "email-template": "bg-primary",
    };
    return badgeMap[type] || "bg-secondary";
  };

  const getTypeLabel = (type: ResourceType): string => {
    const labelMap: Record<ResourceType, string> = {
      project: "Project",
      workflow: "Workflow",
      connection: "Connection",
      schedule: "Schedule",
      product: "Product",
      subscriber: "Subscriber",
      organisation: "Organisation",
      "email-template": "Email Template",
    };
    return labelMap[type] || type;
  };

  const formatTimeAgo = (date: Date | string): string => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    const seconds = Math.floor((Date.now() - dateObj.getTime()) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    return "Just now";
  };

  return {
    trackView,
    removeItem,
    clearAll,
    recentItems,
    getIconForType,
    getBadgeClassForType,
    getTypeLabel,
    formatTimeAgo,
  };
}
